import React, { useState, useEffect, useCallback, useRef } from 'react'
import { Prompt, useHistory, useLocation } from 'react-router-dom'
import Grid from '@mui/material/Grid'
import Paper from '@mui/material/Paper'
import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import MenuItem from '@mui/material/MenuItem'
import Button from '@mui/material/Button'
import FormControl from '@mui/material/FormControl'
import TextField from '@mui/material/TextField'
import AdapterDateFns from '@mui/lab/AdapterDateFns'
import LocalizationProvider from '@mui/lab/LocalizationProvider'
import DesktopDatePicker from '@mui/lab/DesktopDatePicker'
import koLocale from 'date-fns/locale/ko'
import Add from '@mui/icons-material/Add'
import RemoveIcon from '@mui/icons-material/Remove'
import SearchIcon from '@mui/icons-material/Search'

import Title from '../../component/Title'
import API from '../../component/Axios'
import Snackbars from '../../component/Snackbars'
import BottomButtonGroup, { FORM_IMAGE_CLEAR_BUTTON_SX, FORM_INLINE_ACTION_BUTTON_SX, FORM_WIDE_UPLOAD_BUTTON_SX } from '../../component/BottomButtonGroup'
import AddressSearchModal from '../../component/AddressSearchModal'
import Spiner from '../../component/Spiner'
import ListEmptyState from '../../component/ListEmptyState'
import { FORM_IMAGE_PREVIEW_FRAME_SX, FORM_PANEL_SX, FORM_SUB_PANEL_SX } from '../../component/formSurfaceStyles'
import { createListLinkTarget, getLocationState } from '../../utils/listPageState'
import {
    PRODUCT_IMAGE_FILE_ACCEPT,
    PRODUCT_IMAGE_FILE_ERROR_MESSAGE,
    applyProductDraft,
    clearProductDraft,
    createEditProductDraftKey,
    createProductDraftPayload,
    isProductImageFile,
    readProductDraft,
    saveProductDraft,
} from './productDraftUtils'
import {
    PRODUCT_IMAGE_LEAVE_MESSAGE,
    hasPendingProductImages,
    usePendingProductImageUnloadGuard,
} from './productLeaveGuard'

const NO_IMAGE = '/images/noimage.jpg'

// 상세 조회 실패 시에도 폼 기본값을 명확하게 되돌릴 수 있게 공통 초기값을 사용한다.
const createDefaultData = () => ({})

const DEFAULT_SEARCH_SELECT = {
    searchMeetType1: '',
    searchMeetType2: '',
    searchStatus: '',
}

const formSectionSx = {
    ...FORM_PANEL_SX,
    p: 2,
    mb: 1.5,
    boxShadow: 'none',
}

const imagePreviewFrameSx = {
    ...FORM_IMAGE_PREVIEW_FRAME_SX,
    height: { xs: 220, md: 260 },
}

const imagePreviewSx = {
    width: '100%',
    height: '100%',
    objectFit: 'contain',
}

const imageUploadPanelSx = {
    ...FORM_SUB_PANEL_SX,
    p: 1.25,
    minWidth: 0,
}

const imageCardHeaderSx = {
    minHeight: 24,
    display: 'grid',
    gridTemplateColumns: 'minmax(0, 1fr) auto',
    alignItems: 'center',
    columnGap: 1,
}

const courseRowSx = {
    mt: 1.5,
    p: 1.25,
    borderRadius: 1,
    border: '1px solid',
    borderColor: 'divider',
    backgroundColor: 'background.default',
}

// 대회 수정 폼을 운영 목적별 카드로 나눠 애니멀런 화면과 같은 입력 흐름을 유지한다.
function FormSection({ title, description, children, sx = {} }) {
    return (
        <Paper variant="outlined" sx={{ ...formSectionSx, ...sx }}>
            <Box sx={{ mb: 2, pb: 1.5, borderBottom: '1px solid rgba(148, 163, 184, 0.24)' }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 700, color: 'text.primary' }}>
                    {title}
                </Typography>
                {description && (
                    <Typography variant="caption" sx={{ display: 'block', mt: 0.5, color: 'text.secondary' }}>
                        {description}
                    </Typography>
                )}
            </Box>
            {children}
        </Paper>
    )
}

// 이미지 미리보기와 숨김 file input을 한 카드로 묶어 등록/수정 레이아웃을 재사용한다.
function ImageUploadCard({
    title,
    image,
    imageAlt,
    inputId,
    inputName,
    uploadType,
    onUpload,
    canClear = false,
    hasPendingFile = false,
    onClear,
}) {
    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25, ...imageUploadPanelSx }}>
            <Box sx={imageCardHeaderSx}>
                <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.secondary' }}>
                    {title}
                </Typography>
                {canClear && hasPendingFile && (
                    <Button size="small" color="error" variant="text" startIcon={<RemoveIcon />} onClick={onClear} sx={FORM_IMAGE_CLEAR_BUTTON_SX}>
                        선택 취소
                    </Button>
                )}
            </Box>
            <Box sx={imagePreviewFrameSx}>
                <Box component="img" src={image} alt={imageAlt} sx={imagePreviewSx}/>
            </Box>
            <FormControl fullWidth>
                <TextField
                    style={{ display: "none" }}
                    name={inputName}
                    id={inputId}
                    type="file"
                    inputProps={{ accept: PRODUCT_IMAGE_FILE_ACCEPT }}
                    onChange={e => onUpload(e, uploadType)}
                />
                <Box component="label" htmlFor={inputId} sx={{ display: 'block' }}>
                    <Button fullWidth variant="contained" color="success" component="span" startIcon={<Add />} sx={FORM_WIDE_UPLOAD_BUTTON_SX}>
                        이미지 선택
                    </Button>
                </Box>
            </FormControl>
        </Box>
    )
}

const getProductImagePreview = (baseData = {}, imageName) => {
    if (!imageName) {
        return NO_IMAGE
    }

    return imageName.includes('animal')
        ? `${process.env.REACT_APP_AWS_S3_URL}/${imageName}`
        : `${process.env.REACT_APP_SERVER_URL}/product/${baseData.adminId}/${imageName}`
}

export default function Product(props) {
    const history = useHistory()
    const location = useLocation()
    const locationState = getLocationState(location)
    const id = props.match.params.productId
    const searchText = locationState.searchText || ''
    const searchSelect = locationState.searchSelect || DEFAULT_SEARCH_SELECT
    const curPage = locationState.curPage || 1
    const listSearch = location.search || ''
    const draftKey = createEditProductDraftKey(id)
    const [loading, setLoading] = useState(true)
    const [saving, setSaving] = useState(false)
    const [hasDetail, setHasDetail] = useState(false)
    const [draftReady, setDraftReady] = useState(false)
    const [data, setData] = useState(() => createDefaultData())
    const [beginDay, setBeginDay] = useState(new Date())
    // const [beginHours, setBeginHours] = useState(12)
    // const [beginMinutes, setBeginMinutes] = useState(0)
    const [finishDay, setFinishDay] = useState(new Date())
    // const [finishHours, setFinishHours] = useState(12)
    // const [finishMinutes, setFinishMinutes] = useState(0)
    const [mainImage, setMainImage] = useState('')
    const [subImage1, setSubImage1] = useState('')
    const [openSnackbar, setOpenSnackbar] = useState(false)
    const [severitySnackbar, setSeveritySnackbar] = useState('info')
    const [messageSnackbar, setMessageSnackbar] = useState('')
    const [detailErrorMessage, setDetailErrorMessage] = useState('')
    const [arr, setArr] = useState([])
    const [openModal, setOpenModal] = useState(false)
    const initialDraftSnapshotRef = useRef('')
    const baseImagePreviewRef = useRef({
        mainImage: NO_IMAGE,
        subImage1: NO_IMAGE,
    })
    const [navigationUnlocked, setNavigationUnlocked] = useState(false)
    const leaveGuardEnabled = hasPendingProductImages(data) && !saving && !navigationUnlocked

    usePendingProductImageUnloadGuard(leaveGuardEnabled)

    const resetDetailState = useCallback((errorMessage) => {
        setData(createDefaultData())
        setArr([])
        setMainImage(NO_IMAGE)
        setSubImage1(NO_IMAGE)
        setBeginDay(new Date())
        setFinishDay(new Date())
        setOpenModal(false)
        setHasDetail(false)
        setDraftReady(false)
        setDetailErrorMessage(errorMessage)
    }, [])

    const getData = useCallback(async () => {
        setLoading(true)
        setHasDetail(false)
        setDraftReady(false)
        setDetailErrorMessage('')

        if (!id) {
            const errorMessage = '필수 파라미터가 없어 대회 상세를 불러올 수 없습니다.'
            resetDetailState(errorMessage)
            setOpenSnackbar(true)
            setSeveritySnackbar('error')
            setMessageSnackbar(errorMessage)
            setLoading(false)
            return
        }

        try {
            const { data } = await API.post('/api/v1/admin/product/getProduct', {productId: id})
            if (data.code === 1) {
                const baseData = data.data || {}

                if (!baseData.productId) {
                    resetDetailState('대회 상세 정보를 찾을 수 없습니다.')
                    return
                }

                const baseArr = Array.isArray(baseData.product_category) ? baseData.product_category : []
                const baseBeginDay = new Date(baseData.beginTime)
                const safeBeginDay = Number.isNaN(baseBeginDay.getTime()) ? new Date() : baseBeginDay
                const baseFinishDay = new Date(baseData.finishTime)
                const safeFinishDay = Number.isNaN(baseFinishDay.getTime()) ? new Date() : baseFinishDay
                const storedDraft = readProductDraft(draftKey)
                const restoredDraft = applyProductDraft({
                    baseData,
                    baseArr,
                    beginDay: safeBeginDay,
                    finishDay: safeFinishDay,
                    draft: storedDraft,
                })

                initialDraftSnapshotRef.current = JSON.stringify(createProductDraftPayload({
                    data: baseData,
                    arr: baseArr,
                    beginDay: safeBeginDay,
                    finishDay: safeFinishDay,
                }))

                setHasDetail(Boolean(baseData?.productId))
                setData(restoredDraft.data)
                const nextMainImage = getProductImagePreview(baseData, baseData.mainImage)
                const nextSubImage1 = getProductImagePreview(baseData, baseData.subImage1)

                baseImagePreviewRef.current = {
                    mainImage: nextMainImage,
                    subImage1: nextSubImage1,
                }
                setMainImage(nextMainImage)
                setSubImage1(nextSubImage1)
                setBeginDay(restoredDraft.beginDay)
                setFinishDay(restoredDraft.finishDay)
                setArr(restoredDraft.arr)
                setOpenModal(restoredDraft.openModal)
                setDetailErrorMessage('')
                setDraftReady(true)
            } else if (data.code === 403) {
                history.push('/login')
                return
            } else {
                const errorMessage = data.message || '대회 정보를 불러오지 못했습니다.'
                resetDetailState(errorMessage)
                setOpenSnackbar(true)
                setSeveritySnackbar('error')
                setMessageSnackbar(errorMessage)
            }
        } catch (_error) {
            const errorMessage = '대회 정보를 불러오지 못했습니다.'
            resetDetailState(errorMessage)
            setOpenSnackbar(true)
            setSeveritySnackbar('error')
            setMessageSnackbar(errorMessage)
        } finally {
            setLoading(false)
        }
    }, [draftKey, history, id, resetDetailState])

    useEffect(() => {
        getData()
    }, [getData])

    useEffect(() => {
        if (!draftReady || !hasDetail) {
            return
        }

        // 대회 상세는 목록 query를 건드리지 않고 편집 draft만 sessionStorage에 보존한다.
        const nextDraft = createProductDraftPayload({
            data,
            arr,
            beginDay,
            finishDay,
            openModal,
        })
        const nextDraftSnapshot = JSON.stringify(nextDraft)

        if (nextDraftSnapshot === initialDraftSnapshotRef.current) {
            clearProductDraft(draftKey)
            return
        }

        saveProductDraft(draftKey, nextDraft)
    }, [arr, beginDay, data, draftKey, draftReady, finishDay, hasDetail, openModal])

    const handleClose = () => {
        setOpenSnackbar(false)
    }

    const moveToList = (snackbarState) => {
        setNavigationUnlocked(true)
        // 저장 후에도 운영자가 보던 대회 목록 조건으로 그대로 복귀시킨다.
        clearProductDraft(draftKey)

        // 저장 후 이동은 이탈 경고를 잠시 풀고, 목록 조건은 그대로 유지한다.
        setTimeout(() => {
            history.push(createListLinkTarget({
                pathname: '/products',
                search: listSearch,
                searchText: searchText,
                searchSelect: searchSelect,
                curPage: curPage,
                ...snackbarState,
            }))
        }, 0)
    }

    const handleBackClick = (event) => {
        if (leaveGuardEnabled) {
            event?.preventDefault()

            if (!window.confirm(PRODUCT_IMAGE_LEAVE_MESSAGE)) {
                return
            }
        }

        moveToList()
    }

    const addInput = () => {
        setArr(s => {
            return [
                ...s,
                {
                    id: arr[arr.length - 1].id + 1,
                    type: 0,
                    distance: 42,
                    price: 0,
                    name: '',
                    quantity: 0,
                    order: arr[arr.length - 1].order + 1
                }
            ]
        })
    }

    const removeInput = (i) => {
        setArr(arr.filter((item, index) => index !== i))
        // setArr(arr.filter(item => item.id !== i))
    }

    const onChangeOptionType = (e, index) => {
        const value = e.target.value
        let newArr = [...arr]

        if (value === 0) {
            newArr[index].type = 0
            newArr[index].distance = 42
            setArr(newArr)
        } else if (value === 1) {
            newArr[index].type = 1
            newArr[index].distance = 21
            setArr(newArr)
        } else if (value === 2) {
            newArr[index].type = 2
            newArr[index].distance = 0
            setArr(newArr)
        }
    }

    const handleUpdateField = (e) => {
        setData({
            ...data,
            [e.target.name]: e.target.value
        })
    }

    const handleUploadClick = (e, type) => {
        e.preventDefault()
        const file = e.target.files[0]

        if (!file) {
            return
        }

        if (!isProductImageFile(file)) {
            setOpenSnackbar(true)
            setSeveritySnackbar('error')
            setMessageSnackbar(PRODUCT_IMAGE_FILE_ERROR_MESSAGE)
            e.target.value = ''
            return
        }

        const reader = new FileReader()

        reader.onloadend = () => {
            setData((prev) => ({
                ...prev,
                [e.target.name]: file
            }))
            if (type === 1) {
                setMainImage(reader.result)
            } else if (type === 2) {
                setSubImage1(reader.result)
            }
        }
    
        reader.readAsDataURL(file)
    }

    // 선택 이미지 취소는 저장 전 file input과 미리보기만 비운다. 기존 서버 이미지는 그대로 유지한다.
    const handleClearSelectedImage = (fieldName, inputId) => {
        setData((prev) => {
            const nextData = { ...prev }
            delete nextData[fieldName]
            return nextData
        })

        const input = document.getElementById(inputId)
        if (input) {
            input.value = ''
        }

        const fallbackImage = baseImagePreviewRef.current[fieldName] || NO_IMAGE
        if (fieldName === 'subImage1') {
            setSubImage1(fallbackImage)
        }
    }

    const updateData = async (params) => {
        setSaving(true)
        try {
            const { data } = await API({
                method: 'post',
                url: '/api/v1/admin/product/update',
                data: params,
                headers: { "Content-Type": "multipart/form-data" }
            })
            
            if (data.code === 1) {
                moveToList({ openSnackbar: true, severitySnackbar: 'success', messageSnackbar: data.message })
            } else if (data.code === 403) {
                history.push('/login')
            } else {
                setOpenSnackbar(true)
                setSeveritySnackbar('error')
                setMessageSnackbar(data.message)
            }
        } catch (_error) {
            setOpenSnackbar(true)
            setSeveritySnackbar('error')
            setMessageSnackbar('대회를 수정하지 못했습니다.')
        } finally {
            setSaving(false)
        }
    }

    const handleComplete = (res) => {
        let fullAddress = res.address
        let extraAddress = ""

        if (res.addressType === "R") {
            if (res.bname !== "") {
                extraAddress += res.bname
            }
            if (res.buildingName !== "") {
                extraAddress += extraAddress !== "" ? `, ${res.buildingName}` : res.buildingName
            }
            fullAddress += extraAddress !== "" ? ` (${extraAddress})` : ""
        }

        setOpenModal(false)
        setData((prev) => ({
            ...prev,
            country: res.sido,
            area: res.sigungu,
            address: fullAddress,
        }))
    }

    const handleSubmit = async (e) => {
        e.preventDefault()

        if (saving) {
            return
        }
        
        if (arr.length === 0) {
            setOpenSnackbar(true)
            setSeveritySnackbar('error')
            setMessageSnackbar('코스를 등록해주세요.')
            return
        }

        const trimmedTitle = (data.title || '').trim()

        if (!trimmedTitle.length) {
            setOpenSnackbar(true)
            setSeveritySnackbar('error')
            setMessageSnackbar('대회명을 입력해주세요.')
            return
        }

        let nextAddress = data.address
        let nextAddressDetail = data.addressDetail
        let nextLatitude = data.latitude
        let nextLongitude = data.longitude

        if(data.meetType === 2) {
            nextAddress = ''
        }
        if(data.meetType2 === 3) {
            nextAddress = ''
            nextAddressDetail = ''
        }

        const fullAddress = `${nextAddress} ${nextAddressDetail}`.trim()

        if(data.meetType === 1 && fullAddress) {
            const options = {
                method: 'GET',
                headers: {
                    "Content-Type": "application/json; charset=utf-8", 
                    "Authorization": `KakaoAK ${process.env.REACT_APP_KAKAO_API_KEY}`
                }
            }
            let response = await fetch(`https://dapi.kakao.com/v2/local/search/address.json?query=${fullAddress}`, options)
            let result = await response.json()
            nextLatitude = result.documents[0]?.y
            nextLongitude = result.documents[0]?.x
        }

        const formData = new FormData()
        formData.append('productId',data.productId)
        formData.append('adminId',data.adminId)
        if (data.mainImage instanceof File) formData.append('mainImage',data.mainImage)
        if (data.subImage1 instanceof File) formData.append('subImage1',data.subImage1)
        formData.append('meetType',data.meetType)
        formData.append('meetType2',data.meetType2)
        formData.append('title',trimmedTitle)
        formData.append('country',data.country)
        formData.append('area',data.area)
        formData.append('address',nextAddress)
        formData.append('addressDetail',nextAddressDetail)
        formData.append('latitude',nextLatitude)
        formData.append('longitude',nextLongitude)
        formData.append('startTime',data.startTime)
        formData.append('endTime',data.endTime)
        const beginTime = new Date(beginDay)
        beginTime.setHours(0)
        beginTime.setMinutes(0)
        beginTime.setSeconds(0)
        formData.append('beginTime', beginTime)
        const finishTime = new Date(finishDay)
        finishTime.setHours(23)
        finishTime.setMinutes(59)
        finishTime.setSeconds(59)
        formData.append('finishTime', finishTime)
        formData.append('info1',data.info1)
        formData.append('info2',data.info2)
        formData.append('info3',data.info3)
        formData.append('info4',data.info4)
        formData.append('info5',data.info5)
        formData.append('info6',data.info6)
        formData.append('info7',data.info7)
        formData.append('paymentStatus',data.paymentStatus)
        formData.append('couponStatus',data.couponStatus)
        formData.append('visibleStatus',data.visibleStatus)
        formData.append('payQuantity',data.payQuantity)
        formData.append('basicLike',data.basicLike)
        arr.forEach((item, index) => {
            formData.append(`categoryArr[${index}]`, JSON.stringify(item))
        })
        
        updateData(formData)
    }

    const handleClick = (e) => {
        e.target.select()
    }

    // 상세 조회 실패 시 목록 화면과 같은 오류 빈 상태 안내를 사용한다.
    const renderDetailErrorState = () => (
        <ListEmptyState
            variant="error"
            title="대회 상세를 불러오지 못했습니다."
            description={detailErrorMessage || '목록으로 돌아가 다시 확인하세요.'}
            action={(
                <BottomButtonGroup variant="emptyStateAction" onBackClick={handleBackClick} backLabel="목록으로" />
            )}
            sx={{ mt: 2 }}
        />
    )

    return (
        <Grid item xs={12}>
            <AddressSearchModal open={openModal} onClose={() => setOpenModal(false)} onComplete={handleComplete} />
            {loading ? (
                <Grid item xs={12}>
                    <Spiner/>
                    {openSnackbar && <Snackbars open={openSnackbar} severity={severitySnackbar} message={messageSnackbar} handleClose={handleClose}/>}
                </Grid>
            ) : (
                <Paper sx={{ p: 2, display: 'flex', flexDirection: 'column' }}>
                    {!hasDetail ? (
                        <>
                            <Title>대회 수정</Title>
                            {renderDetailErrorState()}
                            {openSnackbar && <Snackbars open={openSnackbar} severity={severitySnackbar} message={messageSnackbar} handleClose={handleClose}/>}
                        </>
                    ) : (
                    <>
                    <Title>대회 수정</Title>
                    <Box component="form" encType="multipart/form-data" onSubmit={handleSubmit} sx={{ opacity: saving ? 0.6 : 1 }} >
                        <Prompt when={leaveGuardEnabled} message={PRODUCT_IMAGE_LEAVE_MESSAGE} />
                        <fieldset disabled={saving} style={{ border: 0, margin: 0, padding: 0, minWidth: 0 }}>
                        <FormSection
                            title="코스 설정"
                            description="코스별 거리, 가격, 판매 수량을 수정합니다."
                        >
                            {arr.map((item, i) => {
                                return (
                                    <Grid container spacing={2} sx={courseRowSx} key={i}>
                                        <Grid item xs={12} sm={6} md={2} >
                                            <FormControl fullWidth sx={{ marginRight:1 }} >
                                                <TextField
                                                    label="코스"
                                                    select
                                                    id={'categoryType'+i}
                                                    name={'categoryType'+i}
                                                    value={item.type}
                                                    onChange={(e) => onChangeOptionType(e, i)}
                                                    size="small"
                                                >
                                                    <MenuItem value={0}>풀코스</MenuItem>
                                                    <MenuItem value={1}>하프코스</MenuItem>
                                                    <MenuItem value={2}>거리입력</MenuItem>
                                                </TextField>
                                            </FormControl>
                                        </Grid>
                                        <Grid item xs={12} sm={6} md={2} >
                                            <FormControl fullWidth sx={{ marginRight:1 }} >
                                                <TextField
                                                    label="거리"
                                                    required
                                                    id={'categoryDistance'+i}
                                                    name={'categoryDistance'+i}
                                                    type="number"
                                                    InputProps={{ inputProps: { min: 0, step: 0.01 } }}
                                                    value={item.distance}
                                                    onChange={(e) => {
                                                        let newArr = [...arr]
                                                        newArr[i].distance = e.target.value
                                                        setArr(newArr)
                                                    }}
                                                    onClick={handleClick}
                                                    size="small"
                                                />
                                            </FormControl>
                                        </Grid>
                                        <Grid item xs={12} sm={6} md={2} >
                                            <FormControl fullWidth sx={{ marginRight:1 }} >
                                                <TextField
                                                    label="가격"
                                                    required
                                                    id={'categoryPrice'+i}
                                                    name={'categoryPrice'+i}
                                                    type="number"
                                                    InputProps={{ inputProps: { min: 0 } }}
                                                    value={item.price}
                                                    onChange={(e) => {
                                                        let newArr = [...arr]
                                                        newArr[i].price = e.target.value
                                                        setArr(newArr)
                                                    }}
                                                    onClick={handleClick}
                                                    size="small"
                                                />
                                            </FormControl>
                                        </Grid>
                                        <Grid item xs={12} sm={6} md={2} >
                                            <FormControl fullWidth sx={{ marginRight:1 }} >
                                                <TextField
                                                    label="코스명"
                                                    id={'categoryName'+i}
                                                    name={'categoryName'+i}
                                                    value={item.name}
                                                    onChange={(e) => {
                                                        let newArr = [...arr]
                                                        newArr[i].name = e.target.value
                                                        setArr(newArr)
                                                    }}
                                                    size="small"
                                                />
                                            </FormControl>
                                        </Grid>
                                        <Grid item xs={12} sm={6} md={2} >
                                            <FormControl fullWidth sx={{ marginRight:1 }} >
                                                <TextField
                                                    label="수량"
                                                    required
                                                    id={'categoryQuantity'+i}
                                                    name={'categoryQuantity'+i}
                                                    type="number"
                                                    InputProps={{ inputProps: { min: 0, step: 1 } }}
                                                    value={item.quantity}
                                                    onChange={(e) => {
                                                        let newArr = [...arr]
                                                        newArr[i].quantity = e.target.value
                                                        setArr(newArr)
                                                    }}
                                                    onClick={handleClick}
                                                    size="small"
                                                />
                                            </FormControl>
                                        </Grid>
                                        {i > 0 &&
                                            <Grid item xs={12} sm={6} md={2} >
                                                <Button variant="outlined" color="error" sx={{ ...FORM_INLINE_ACTION_BUTTON_SX, minWidth: 98 }} onClick={()=>removeInput(i)} startIcon={<RemoveIcon />}>코스삭제</Button>
                                            </Grid>
                                        }
                                    </Grid>
                                )
                            })}
                            <Box sx={{ mt: 1}}>
                                <Button variant="contained" color="success" sx={{ ...FORM_INLINE_ACTION_BUTTON_SX, minWidth: 98 }} onClick={addInput} startIcon={<Add />}>코스추가</Button>
                            </Box>
                        </FormSection>

                        <FormSection
                            title="이미지"
                            description="메인이미지와 상세이미지를 한 줄에서 확인하고 교체합니다."
                        >
                            <Grid container spacing={2}>
                                <Grid item xs={12} sm={6} md={6}>
                                    <ImageUploadCard
                                        title="메인이미지"
                                        image={mainImage}
                                        imageAlt="메인이미지"
                                        inputId="mainImage"
                                        inputName="mainImage"
                                        uploadType={1}
                                        onUpload={handleUploadClick}
                                    />
                                </Grid>
                                <Grid item xs={12} sm={6} md={6}>
                                    <ImageUploadCard
                                        title="상세이미지"
                                        image={subImage1}
                                        imageAlt="상세이미지"
                                        inputId="subImage1"
                                        inputName="subImage1"
                                        uploadType={2}
                                        onUpload={handleUploadClick}
                                        canClear
                                        hasPendingFile={data.subImage1 instanceof File}
                                        onClear={() => handleClearSelectedImage('subImage1', 'subImage1')}
                                    />
                                </Grid>
                            </Grid>
                        </FormSection>

                        <FormSection
                            title="일정"
                            description="대회 종류와 접수/진행 기간을 설정합니다."
                        >
                            <Grid container spacing={2}>
                                <Grid item xs={6} md={6} sm={6} >
                                    <FormControl fullWidth>
                                        <TextField
                                            label="대회종류"
                                            select
                                            id="meetType"
                                            name="meetType"
                                            value={data.meetType}
                                            onChange={handleUpdateField}
                                            size="small"
                                        >
                                            <MenuItem value={1}>국내대회</MenuItem>
                                            <MenuItem value={2}>해외대회</MenuItem>
                                        </TextField>
                                    </FormControl>
                                </Grid>
                                <Grid item xs={6} md={6} sm={6} >
                                    <FormControl fullWidth>
                                        <TextField
                                            label="대회타입"
                                            select
                                            id="meetType2"
                                            name="meetType2"
                                            value={data.meetType2}
                                            onChange={handleUpdateField}
                                            size="small"
                                        >
                                            <MenuItem value={1}>마라톤</MenuItem>
                                            <MenuItem value={2}>트레일러닝</MenuItem>
                                            <MenuItem value={3}>버추얼레이스</MenuItem>
                                        </TextField>
                                    </FormControl>
                                </Grid>
                                <Grid item xs={6} md={6} sm={6}>
                                    <FormControl fullWidth>
                                        <LocalizationProvider dateAdapter={AdapterDateFns} locale={koLocale}>
                                            <DesktopDatePicker
                                                label="접수시작일"
                                                inputFormat="yyyy-MM-dd"
                                                mask={"____-__-__"}
                                                value={data.startTime}
                                                onChange={d => setData({
                                                    ...data,
                                                    startTime: d
                                                })}
                                                renderInput={(params) => <TextField required {...params} size="small"/>}
                                            />
                                        </LocalizationProvider>
                                    </FormControl>
                                </Grid>
                                <Grid item xs={6} md={6} sm={6}>
                                    <FormControl fullWidth>
                                        <LocalizationProvider dateAdapter={AdapterDateFns} locale={koLocale}>
                                            <DesktopDatePicker
                                                label="접수종료일"
                                                inputFormat="yyyy-MM-dd"
                                                mask={"____-__-__"}
                                                value={data.endTime}
                                                minDate={new Date(data.startTime)}
                                                onChange={d => setData({
                                                    ...data,
                                                    endTime: d
                                                })}
                                                renderInput={(params) => <TextField required {...params} size="small" />}
                                            />
                                        </LocalizationProvider>
                                    </FormControl>
                                </Grid>
                                <Grid item xs={6} md={6} sm={6}>
                                    <FormControl fullWidth>
                                        <LocalizationProvider dateAdapter={AdapterDateFns} locale={koLocale}>
                                            <DesktopDatePicker
                                                label="대회시작일"
                                                inputFormat="yyyy-MM-dd"
                                                mask={"____-__-__"}
                                                value={beginDay}
                                                onChange={d => setBeginDay(d)}
                                                renderInput={(params) => <TextField required {...params} size="small" />}
                                            />
                                        </LocalizationProvider>
                                    </FormControl>
                                </Grid>
                                <Grid item xs={6} md={6} sm={6}>
                                    <FormControl fullWidth>
                                        <LocalizationProvider dateAdapter={AdapterDateFns} locale={koLocale}>
                                            <DesktopDatePicker
                                                label="대회종료일"
                                                inputFormat="yyyy-MM-dd"
                                                mask={"____-__-__"}
                                                value={finishDay}
                                                minDate={beginDay}
                                                onChange={d => setFinishDay(d)}
                                                renderInput={(params) => <TextField required {...params} size="small" />}
                                            />
                                        </LocalizationProvider>
                                    </FormControl>
                                </Grid>
                            </Grid>
                        </FormSection>

                        <FormSection
                            title="대회 정보"
                            description="앱에 노출할 기본 문구와 문의 정보를 입력합니다."
                        >
                            <Grid container spacing={2}>
                                <Grid item xs={12} sm={12} md={12}>
                                    <FormControl fullWidth>
                                        <TextField
                                            label="대회명"
                                            required
                                            id="title"
                                            name="title"
                                            value={data.title}
                                            onChange={handleUpdateField}
                                            size="small"
                                        />
                                    </FormControl>
                                </Grid>
                                {data.meetType === 2 &&
                                    <>
                                    <Grid item xs={6} md={6} sm={6}>
                                        <FormControl fullWidth>
                                            <TextField
                                                label="국가명"
                                                required
                                                id="country"
                                                name="country"
                                                value={data.country}
                                                onChange={handleUpdateField}
                                                size="small"
                                            />
                                        </FormControl>
                                    </Grid>
                                    <Grid item xs={6} md={6} sm={6}>
                                        <FormControl fullWidth>
                                            <TextField
                                                label="도시"
                                                required
                                                id="area"
                                                name="area"
                                                value={data.area}
                                                onChange={handleUpdateField}
                                                size="small"
                                            />
                                        </FormControl>
                                    </Grid>
                                    </>
                                }
                                {data.meetType2 !== 3 && data.meetType === 1 &&
                                    <Grid item xs={6} md={6} sm={6}>
                                        <FormControl fullWidth>
                                            <TextField
                                                label="주소"
                                                required
                                                id="address"
                                                name="address"
                                                value={data.address}
                                                size="small"
                                                onClick={() => setOpenModal(true)}
                                                InputProps={{ readOnly: true, endAdornment: <SearchIcon color="action"/>}}
                                            />
                                        </FormControl>
                                    </Grid>
                                }
                                {data.meetType2 !== 3 &&
                                    <Grid item xs={data.meetType === 1 ? 6 : 12} md={data.meetType === 1 ? 6 : 12} sm={data.meetType === 1 ? 6 : 12}>
                                        <FormControl fullWidth>
                                            <TextField
                                                label="상세주소"
                                                id="addressDetail"
                                                name="addressDetail"
                                                value={data.addressDetail}
                                                onChange={handleUpdateField}
                                                size="small"
                                            />
                                        </FormControl>
                                    </Grid>
                                }
                                <Grid item xs={12} sm={6} md={3}>
                                    <FormControl fullWidth>
                                        <TextField
                                            label="문의"
                                            id="info2"
                                            name="info2"
                                            value={data.info2}
                                            onChange={handleUpdateField}
                                            size="small"
                                        />
                                    </FormControl>
                                </Grid>
                                <Grid item xs={12} sm={6} md={3}>
                                    <FormControl fullWidth>
                                        <TextField
                                            label="주최"
                                            id="info4"
                                            name="info4"
                                            value={data.info4}
                                            onChange={handleUpdateField}
                                            size="small"
                                        />
                                    </FormControl>
                                </Grid>
                                <Grid item xs={12} sm={6} md={3}>
                                    <FormControl fullWidth>
                                        <TextField
                                            label="홈페이지"
                                            id="info1"
                                            name="info1"
                                            value={data.info1}
                                            onChange={handleUpdateField}
                                            size="small"
                                        />
                                    </FormControl>
                                </Grid>
                                <Grid item xs={12} sm={6} md={3}>
                                    <FormControl fullWidth>
                                        <TextField
                                            label="협찬"
                                            id="info3"
                                            name="info3"
                                            value={data.info3}
                                            onChange={handleUpdateField}
                                            size="small"
                                        />
                                    </FormControl>
                                </Grid>
                            </Grid>
                        </FormSection>

                        <FormSection
                            title="판매/노출"
                            description="앱 노출 방식과 기본 지표를 설정합니다."
                        >
                            <Grid container spacing={2}>
                                <Grid item xs={12} sm={6} md={3}>
                                    <FormControl fullWidth>
                                        <TextField
                                            label="거리/코스명"
                                            select
                                            id="visibleStatus"
                                            name="visibleStatus"
                                            value={data.visibleStatus}
                                            onChange={handleUpdateField}
                                            size="small"
                                        >
                                            <MenuItem value={0}>거리 사용</MenuItem>
                                            <MenuItem value={1}>코스명 사용</MenuItem>
                                        </TextField>
                                    </FormControl>
                                </Grid>
                                <Grid item xs={12} sm={6} md={3}>
                                    <FormControl fullWidth>
                                        <TextField
                                            label="기본좋아요수"
                                            type="number"
                                            id="basicLike"
                                            name="basicLike"
                                            value={data.basicLike}
                                            onChange={handleUpdateField}
                                            onClick={handleClick}
                                            size="small"
                                            InputProps={{ inputProps: { min: 0, step: 1 } }}
                                        />
                                    </FormControl>
                                </Grid>
                            </Grid>
                        </FormSection>
                        </fieldset>
                        <BottomButtonGroup
                            variant="formAction"
                            visible={true}
                            submitDisabled={saving}
                            submitLabel={saving ? '저장 중...' : '저장'}
                            backDisabled={saving}
                            onBackClick={handleBackClick}
                        />
                    </Box>
                    {openSnackbar && <Snackbars open={openSnackbar} severity={severitySnackbar} message={messageSnackbar} handleClose={handleClose}/>}
                    </>
                    )}
                </Paper>
            )}
        </Grid>
    )
}
